import { Type } from '@angular/core';
import { BadgeSingleColorWidgetComponent } from './badge-single-color/badge-single-color-widget.component';
import { BadgeMultiColorWidgetComponent } from './badge-multi-color/badge-multi-color-widget.component';
import { StarsWidgetComponent } from './stars/stars-widget.component';
import { ProgressBarWidgetComponent } from './progress-bar/progress-bar-widget.component';
import { LinkWidgetComponent } from './link/link-widget.component';
import { BooleanCheckWidgetComponent } from './boolean-check/boolean-check-widget.component';
import { BooleanYesNoWidgetComponent } from './boolean-yes-no/boolean-yes-no-widget.component';
import {
  BadgeColor,
  BadgeSingleColorWidgetConfig,
} from './badge-single-color/model';
import { BadgeMultiColorWidgetConfig } from './badge-multi-color/model';
import { StarsWidgetConfig } from './stars/model';
import { ProgressBarWidgetConfig } from './progress-bar/model';
import { LinkWidgetConfig } from './link/model';
import { BooleanCheckWidgetConfig } from './boolean-check/model';
import { BooleanYesNoWidgetConfig } from './boolean-yes-no/model';

/**
 * Generic Widget Definition class that encapsulates a widget component and its configuration.
 * Provides type-safe access to both the component type and its associated configuration.
 */
class WidgetDefinition<TWidgetComponent, TWidgetComponentConfig> {
  constructor(
    readonly widgetComponent: TWidgetComponent,
    readonly configs: TWidgetComponentConfig,
  ) {}
}

/**
 * Exported type alias for use in type annotations.
 * Allows referencing WidgetDefinition with full generic type information.
 */
export type WidgetDefinitionType<TWidgetComponent, TWidgetComponentConfig> =
  WidgetDefinition<TWidgetComponent, TWidgetComponentConfig>;

/**
 * Re-export configuration interfaces from their respective model files for convenience.
 */
export type { BadgeSingleColorWidgetConfig } from './badge-single-color/model';
export type { BadgeMultiColorWidgetConfig } from './badge-multi-color/model';
export type { StarsWidgetConfig } from './stars/model';
export type { ProgressBarWidgetConfig } from './progress-bar/model';
export type { LinkWidgetConfig } from './link/model';
export type { BooleanCheckWidgetConfig } from './boolean-check/model';
export type { BooleanYesNoWidgetConfig } from './boolean-yes-no/model';
export type { BadgeColor } from './badge-single-color/model';

/**
 * Widget Factory - Static class that centralizes all widget factories.
 * Each static method returns a configured WidgetDefinition.
 */
export class Widgets {
  /**
   * Creates a badge widget with a single color.
   * Supports predefined colors, custom hex colors, required tooltips, and optional action callbacks.
   * Falls back to static gray when neither color nor customColor is set.
   * @param config Badge configuration (color, customColor, required tooltip, optional action)
   * @returns WidgetDefinition to use in columns
   *
   * @example
   * // Basic usage with required tooltip
   * widget: Widgets.BadgeSingleColor({
   *   color: "primary",
   *   tooltip: "Primary badge"
   * })
   *
   * @example
   * // Using custom hex color with tooltip
   * widget: Widgets.BadgeSingleColor({
   *   customColor: "#FF5733",
   *   tooltip: "Custom color badge"
   * })
   *
   * @example
   * // Badge with action callback on click
   * widget: Widgets.BadgeSingleColor({
   *   color: "success",
   *   tooltip: "Click to approve",
   *   action: (value) => this.approveItem(value)
   * })
   *
   * @example
   * // Badge with i18n translation support
   * widget: Widgets.BadgeSingleColor({
   *   color: "success",
   *   tooltip: "Role",
   *   translateKey: "ROLE",
   *   translationPrefix: "USERS_DEMO_2.USERS_TABLE"
   *   // Resolves to: USERS_DEMO_2.USERS_TABLE.ROLE.ADMIN
   * })
   */
  static BadgeSingleColor(
    config: BadgeSingleColorWidgetConfig,
  ): WidgetDefinition<
    typeof BadgeSingleColorWidgetComponent,
    BadgeSingleColorWidgetConfig
  > {
    return new WidgetDefinition(BadgeSingleColorWidgetComponent, {
      color: config.color,
      customColor: config.customColor,
      tooltip: config.tooltip,
      icon: config.icon,
      action: config.action,
      translate: config.translate,
    });
  }

  /**
   * Creates a badge widget with multiple colors based on the value.
   * Choose EITHER colorMap (direct mapping) OR evaluator (conditional logic), not both.
   * Tooltip is optional. Supports optional action callbacks.
   *
   * @param config Badge configuration - use colorMap OR evaluator (mutually exclusive), plus required tooltip, optional action
   * @returns WidgetDefinition to use in columns
   *
   * @example
   * // Using colorMap for direct value mapping with tooltip
   * widget: Widgets.BadgeMultiColor({
   *   colorMap: {
   *     "ADMIN": "danger",
   *     "ANALYST": "primary",
   *     "USER": "success"
   *   },
   *   tooltip: "User role classification"
   * })
   *
   * @example
   * // Using colorMap with numeric values (converted to strings)
   * widget: Widgets.BadgeMultiColor({
   *   colorMap: {
   *     "85000": "danger",
   *     "50000": "warning",
   *     "30000": "success"
   *   },
   *   tooltip: "Salary range"
   * })
   *
   * @example
   * // Using evaluator function for conditional logic (RECOMMENDED for ranges)
   * widget: Widgets.BadgeMultiColor({
   *   evaluator: (value) => {
   *     const num = Number(value);
   *     if (num > 80000) return "danger";
   *     if (num > 50000) return "warning";
   *     return "success";
   *   },
   *   tooltip: "Salary classification"
   * })
   *
   * @example
   * // Evaluator with custom hex colors and action callback
   * widget: Widgets.BadgeMultiColor({
   *   evaluator: (value) => {
   *     const status = String(value).toLowerCase();
   *     if (status.includes("admin")) return "#FF5733";
   *     if (status.includes("analyst")) return "#4CAF50";
   *     return "#2196F3";
   *   },
   *   tooltip: "Status indicator",
   *   action: (value) => this.editUser(value)
   * })
   *
   * @example
   * // BadgeMultiColor with i18n translation support
   * widget: Widgets.BadgeMultiColor({
   *   colorMap: {
   *     "ADMIN": "danger",
   *     "ANALYST": "primary",
   *     "USER": "success"
   *   },
   *   tooltip: "User role",
   *   translateKey: "ROLE",
   *   translationPrefix: "USERS_DEMO_2.USERS_TABLE"
   *   // Resolves to: USERS_DEMO_2.USERS_TABLE.ROLE.ADMIN
   * })
   */
  static BadgeMultiColor(
    config: BadgeMultiColorWidgetConfig,
  ): WidgetDefinition<
    typeof BadgeMultiColorWidgetComponent,
    BadgeMultiColorWidgetConfig
  > {
    if ('evaluator' in config) {
      return new WidgetDefinition(BadgeMultiColorWidgetComponent, {
        evaluator: config.evaluator,
        tooltip: config.tooltip,
        icon: config.icon,
        action: config.action,
        translate: config.translate,
        translationKey: config.translationKey,
      } as BadgeMultiColorWidgetConfig);
    } else {
      return new WidgetDefinition(BadgeMultiColorWidgetComponent, {
        colorMap: config.colorMap,
        tooltip: config.tooltip,
        icon: config.icon,
        action: config.action,
        translate: config.translate,
        translationKey: config.translationKey,
      } as BadgeMultiColorWidgetConfig);
    }
  }

  /**
   * Creates a star rating widget.
   * Displays 5 stars based on a numeric value and optional base, supporting partial stars.
   * Calculates percentage as: (value / max) * 100
   * Supports predefined colors or custom hex colors; falls back to static gray when unset.
   * Supports optional action callbacks on click.
   * @param config Star widget configuration (required tooltip, optional max, color or customColor, action)
   * @returns WidgetDefinition to use in columns
   *
   * @example
   * // Basic star rating with default base 100 (percentage 0-100)
   * widget: Widgets.Stars({
   *   tooltip: "Performance rating"
   * })
   *
   * @example
   * // Star rating with custom base: value=4, max=5 (4/5 = 80%)
   * widget: Widgets.Stars({
   *   tooltip: "User satisfaction",
   *   max: 5
   * })
   *
   * @example
   * // Star rating with large base: value=200, max=1000 (200/1000 = 20%)
   * widget: Widgets.Stars({
   *   tooltip: "Score rating",
   *   max: 1000,
   *   color: "success"
   * })
   *
   * @example
   * // Star rating with custom hex color and action callback
   * widget: Widgets.Stars({
   *   tooltip: "Quality rating",
   *   max: 10,
   *   customColor: "#FF5733",
   *   action: (value) => this.updateQualityRating(value)
   * })
   */
  static Stars(
    config: StarsWidgetConfig,
  ): WidgetDefinition<typeof StarsWidgetComponent, StarsWidgetConfig> {
    return new WidgetDefinition(StarsWidgetComponent, {
      tooltip: config.tooltip,
      max: config.max || 100,
      color: config.color,
      customColor: config.customColor,
      action: config.action,
    });
  }

  /**
   * Creates a progress bar widget.
   * Displays a horizontal progress bar with optional conditional coloring.
   * Calculates percentage as: (value / max) * 100
   * Supports conditional colors via evaluator function or fixed colors.
   * Supports optional action callbacks on click.
   * @param config Progress bar configuration (required tooltip, optional max, evaluator, defaultColor or customColor, width, action)
   * @returns WidgetDefinition to use in columns
   *
   * @example
   * // Basic progress bar with static gray fallback
   * widget: Widgets.ProgressBar({
   *   tooltip: "Completion progress"
   * })
   *
   * @example
   * // Progress bar with conditional colors based on percentage
   * widget: Widgets.ProgressBar({
   *   tooltip: "Performance level",
   *   evaluator: (percentage) => {
   *     if (percentage < 20) return "danger";
   *     if (percentage < 50) return "warning";
   *     if (percentage < 80) return "info";
   *     return "success";
   *   }
   * })
   *
   * @example
   * // Progress bar with custom width (200 pixels)
   * widget: Widgets.ProgressBar({
   *   tooltip: "Download progress",
   *   defaultColor: "success",
   *   width: "200px"
   * })
   *
   * @example
   * // Progress bar with custom base (e.g., 0-500 range), width, and action callback
   * widget: Widgets.ProgressBar({
   *   tooltip: "Score progress (0-500)",
   *   max: 500,
   *   width: "50%",
   *   evaluator: (percentage) => {
   *     if (percentage < 33) return "#FF6B6B";  // Red hex
   *     if (percentage < 66) return "#FFA500";  // Orange hex
   *     return "#51CF66";                       // Green hex
   *   },
   *   action: (value) => this.reviewProgress(value)
   * })
   *
   * @example
   * // Progress bar with custom width in rem units
   * widget: Widgets.ProgressBar({
   *   tooltip: "Task completion",
   *   width: "20rem",
   *   evaluator: (percentage) => {
   *     if (percentage < 50) return "warning";
   *     return "success";
   *   }
   * })
   */
  static ProgressBar(
    config: ProgressBarWidgetConfig,
  ): WidgetDefinition<
    typeof ProgressBarWidgetComponent,
    ProgressBarWidgetConfig
  > {
    return new WidgetDefinition(ProgressBarWidgetComponent, {
      tooltip: config.tooltip,
      max: config.max || 100,
      evaluator: config.evaluator,
      defaultColor: config.defaultColor,
      customColor: config.customColor,
      width: config.width || '100%',
      action: config.action,
    });
  }

  /**
   * Creates a clickable link widget that executes an action callback.
   * Displays the value as a clickable link/button that triggers a custom action.
   * Useful for editing, deleting, opening links, or performing any custom action on a row.
   * @param config Link widget configuration (required action function, optional tooltip, icon, color, translate)
   * @returns WidgetDefinition to use in columns
   *
   * @example
   * // Basic link with action
   * widget: Widgets.Link({
   *   action: (value) => console.log('Clicked:', value)
   * })
   *
   * @example
   * // Link with icon and tooltip
   * widget: Widgets.Link({
   *   action: (value) => this.router.navigate(['/edit', value]),
   *   icon: "edit",
   *   tooltip: "EDIT_ACTION"
   * })
   *
   * @example
   * // Delete link with dynamic tooltip
   * widget: Widgets.Link({
   *   action: (value) => this.deleteItem(value),
   *   icon: "delete",
   *   color: "#FF5733",
   *   tooltip: (value) => `Delete ${value}`
   * })
   *
   * @example
   * // Link with translation support
   * widget: Widgets.Link({
   *   action: (value) => window.open(value, '_blank'),
   *   icon: "file_download",
   *   color: "primary",
   *   translate: true,
   *   tooltip: "DOWNLOAD_ACTION"
   * })
   */
  static Link(
    config: LinkWidgetConfig,
  ): WidgetDefinition<typeof LinkWidgetComponent, LinkWidgetConfig> {
    return new WidgetDefinition(LinkWidgetComponent, {
      action: config.action,
      tooltip: config.tooltip,
      translate: config.translate,
      icon: config.icon,
      color: config.color,
    });
  }

  /**
   * Creates a boolean check widget.
   * Displays a checked or unchecked icon based on a boolean value.
   * Checked state shows a filled checkbox icon, unchecked state shows an empty checkbox icon.
   * @param config Boolean check widget configuration (optional tooltip, colors, action, translate)
   * @returns WidgetDefinition to use in columns
   *
   * @example
   * // Basic boolean check without tooltip
   * widget: Widgets.BooleanCheck({
   *   checkedColor: 'success',
   *   uncheckedColor: 'danger'
   * })
   *
   * @example
   * // Boolean check with static tooltip
   * widget: Widgets.BooleanCheck({
   *   tooltip: "VERIFICATION_STATUS",
   *   checkedColor: "success",
   *   uncheckedColor: "warning"
   * })
   *
   * @example
   * // Boolean check with dynamic tooltip
   * widget: Widgets.BooleanCheck({
   *   tooltip: (value) => value ? "Enabled" : "Disabled",
   *   action: (value) => this.toggleFeature(value)
   * })
   *
   * @example
   * // Boolean check with i18n support
   * widget: Widgets.BooleanCheck({
   *   translate: true,
   *   tooltip: "TWO_FACTOR_TOOLTIP",
   *   action: (value) => this.toggleTwoFactor(value)
   * })
   */
  static BooleanCheck(
    config: BooleanCheckWidgetConfig,
  ): WidgetDefinition<
    typeof BooleanCheckWidgetComponent,
    BooleanCheckWidgetConfig
  > {
    return new WidgetDefinition(BooleanCheckWidgetComponent, {
      translate: config.translate,
      tooltip: config.tooltip,
      checkedColor: config.checkedColor,
      uncheckedColor: config.uncheckedColor,
      action: config.action,
    });
  }

  /**
   * Creates a boolean Yes/No widget.
   * Displays "Yes" or "No" text based on a boolean value with full i18n support.
   * Perfect for displaying boolean values in human-readable format with translation support.
   * Always uses YES/NO translation keys - use translationPrefix to build full translation keys.
   * @param config Boolean Yes/No widget configuration (optional translationPrefix, colors, tooltip, action)
   * @returns WidgetDefinition to use in columns
   *
   * @example
   * // Simplest usage - uses default YES/NO translation keys
   * widget: (item) => Widgets.BooleanYesNo({})
   *
   * @example
   * // With translation prefix to resolve keys from section
   * widget: (item) => Widgets.BooleanYesNo({
   *   translationPrefix: 'USERS_DEMO_2.USERS_TABLE'
   * })
   *
   * @example
   * // With custom colors and prefix
   * widget: (item) => Widgets.BooleanYesNo({
   *   translationPrefix: 'USERS_DEMO_2.USERS_TABLE',
   *   yesColor: 'success',
   *   noColor: 'danger'
   * })
   *
   * @example
   * // With tooltip, colors and prefix
   * widget: (item) => Widgets.BooleanYesNo({
   *   translationPrefix: 'USERS_DEMO_2.USERS_TABLE',
   *   tooltip: (value) => value ? 'Email verified' : 'Email not verified',
   *   action: (value) => this.updateVerificationStatus(value)
   * })
   */
  static BooleanYesNo(
    config: BooleanYesNoWidgetConfig,
  ): WidgetDefinition<
    typeof BooleanYesNoWidgetComponent,
    BooleanYesNoWidgetConfig
  > {
    return new WidgetDefinition(BooleanYesNoWidgetComponent, {
      translate: config.translate,
      yesColor: config.yesColor,
      noColor: config.noColor,
      tooltip: config.tooltip,
      action: config.action,
    });
  }
}
